import React, { useState, useEffect } from 'react';
import { Competition, Solution } from '../models';
import { CompetitionSearchQuery, SolutionSearchQuery, SimilarityType } from '../services/DataService';
import { LocalStorageDataService } from '../services/LocalStorageDataService';

/**
 * 検索コンテキストのインターフェース
 */
interface SearchContextType {
  // 検索モード
  searchMode: 'competition' | 'solution';
  setSearchMode: (mode: 'competition' | 'solution') => void;
  
  // 検索クエリ
  competitionQuery: CompetitionSearchQuery;
  setCompetitionQuery: (query: CompetitionSearchQuery) => void;
  solutionQuery: SolutionSearchQuery;
  setSolutionQuery: (query: SolutionSearchQuery) => void;
  
  // 検索結果
  competitionResults: Competition[];
  solutionResults: Solution[];
  
  // 類似性検索
  similarityType: SimilarityType;
  setSimilarityType: (type: SimilarityType) => void;
  findSimilarCompetitions: (slug: string) => Promise<void>;
  
  // 検索実行
  performSearch: () => Promise<void>;
  
  // ローディング状態
  isLoading: boolean;
}

/**
 * 検索コンテキストの作成
 */
export const SearchContext = React.createContext<SearchContextType | undefined>(undefined);

/**
 * 検索プロバイダーコンポーネント
 */
export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // データサービス
  const dataService = new LocalStorageDataService();
  
  // 検索モード
  const [searchMode, setSearchMode] = useState<'competition' | 'solution'>('competition');
  
  // 検索クエリ
  const [competitionQuery, setCompetitionQuery] = useState<CompetitionSearchQuery>({});
  const [solutionQuery, setSolutionQuery] = useState<SolutionSearchQuery>({});
  
  // 検索結果
  const [competitionResults, setCompetitionResults] = useState<Competition[]>([]);
  const [solutionResults, setSolutionResults] = useState<Solution[]>([]);
  
  // 類似性検索
  const [similarityType, setSimilarityType] = useState<SimilarityType>(SimilarityType.ALL);
  
  // ローディング状態
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  /**
   * 検索実行
   */
  const performSearch = async (): Promise<void> => {
    setIsLoading(true);
    
    try {
      if (searchMode === 'competition') {
        const results = await dataService.searchCompetitions(competitionQuery);
        setCompetitionResults(results);
      } else {
        const results = await dataService.searchSolutions(solutionQuery);
        setSolutionResults(results);
      }
    } catch (error) {
      console.error('検索中にエラーが発生しました:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * 類似コンペティションの検索
   */
  const findSimilarCompetitions = async (slug: string): Promise<void> => {
    setIsLoading(true);
    
    try {
      const results = await dataService.findSimilarCompetitions(slug, similarityType);
      setCompetitionResults(results);
      setSearchMode('competition');
    } catch (error) {
      console.error('類似コンペの検索中にエラーが発生しました:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // コンテキスト値
  const contextValue: SearchContextType = {
    searchMode,
    setSearchMode,
    competitionQuery,
    setCompetitionQuery,
    solutionQuery,
    setSolutionQuery,
    competitionResults,
    solutionResults,
    similarityType,
    setSimilarityType,
    findSimilarCompetitions,
    performSearch,
    isLoading
  };
  
  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};
